import React, { useEffect, useState } from "react";
import { Landmark, Save, Smartphone } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const EMPTY = { account_holder_name: "", account_number: "", ifsc_code: "", bank_name: "", upi_id: "" };

export default function BankDetailsSection({ user, onSaved, id }) {
  const [bank, setBank] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [method, setMethod] = useState("bank");
  const [form, setForm] = useState({ ...EMPTY, account_holder_name: user?.name || "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/users/me/bank").then(({ data }) => {
      if (data?.payout_method) {
        setBank(data);
        setMethod(data.payout_method);
        setForm((f) => ({ ...f, account_holder_name: data.account_holder_name || f.account_holder_name, ifsc_code: data.ifsc_code || "", bank_name: data.bank_name || "", upi_id: data.upi_id || "" }));
      }
    }).catch(() => {});
  }, [user?.id]);

  const save = async () => {
    setBusy(true);
    try {
      const body = { account_holder_name: form.account_holder_name };
      if (method === "bank") { body.account_number = form.account_number.trim(); body.ifsc_code = form.ifsc_code.trim().toUpperCase(); body.bank_name = form.bank_name; }
      else body.upi_id = form.upi_id.trim();
      const { data } = await api.put("/users/me/bank", body);
      setBank(data);
      setShowForm(false);
      toast.success("Payout details saved securely");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div id={id} className={`mt-4 rounded-lg border p-5 ${bank ? "border-[#2B3A67]/20 bg-[#D4DEC6]/25" : "border-[#C85A32]/40 bg-[#F0EBE1]"}`} data-testid="bank-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Landmark size={16} className="text-[#2B3A67]" />
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-[#2B3A67] font-semibold">Bank / UPI for payouts</div>
            {bank ? (
              <div className="text-sm text-[#1A1F2C] mt-1" data-testid="bank-summary">
                {bank.account_holder_name}
                {bank.account_number_masked && <> · {bank.bank_name || "Bank"} · <span className="font-mono" data-testid="bank-masked">{bank.account_number_masked}</span> · <span className="font-mono">{bank.ifsc_code}</span></>}
                {bank.upi_id && <> · UPI <span className="font-mono" data-testid="bank-upi">{bank.upi_id}</span></>}
              </div>
            ) : (
              <div className="text-sm text-[#1A1F2C] mt-1">Add a bank account or UPI ID so LocaNest can transfer tenant payments to you within 3–4 days.</div>
            )}
          </div>
        </div>
        <Button
          onClick={() => setShowForm((v) => !v)}
          variant={bank ? "outline" : "default"}
          className={`rounded-md ${bank ? "" : "bg-[#C85A32] hover:bg-[#a8482a] text-white"}`}
          data-testid="bank-open-form"
        >
          {bank ? "Update payout details" : "Add bank / UPI"}
        </Button>
      </div>

      {showForm && (
        <div className="mt-4 space-y-3">
          <div className="inline-flex rounded-md bg-[#FDFBF7] border border-[#1A1F2C]/10 p-1 text-sm" data-testid="payout-method-toggle">
            <button type="button" onClick={() => setMethod("bank")} className={`px-3 py-1.5 rounded inline-flex items-center gap-1.5 ${method === "bank" ? "bg-[#2B3A67] text-white" : "text-[#5C6B7A]"}`} data-testid="payout-method-bank"><Landmark size={13} /> Bank account</button>
            <button type="button" onClick={() => setMethod("upi")} className={`px-3 py-1.5 rounded inline-flex items-center gap-1.5 ${method === "upi" ? "bg-[#2B3A67] text-white" : "text-[#5C6B7A]"}`} data-testid="payout-method-upi"><Smartphone size={13} /> UPI ID</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Account holder name</Label>
              <Input value={form.account_holder_name} onChange={(e) => setForm({ ...form, account_holder_name: e.target.value })} data-testid="bank-holder" />
            </div>
            {method === "bank" ? (
              <>
                <div>
                  <Label>Account number</Label>
                  <Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value.replace(/\D/g, "") })} placeholder="Digits only" data-testid="bank-account" />
                </div>
                <div>
                  <Label>IFSC code</Label>
                  <Input value={form.ifsc_code} onChange={(e) => setForm({ ...form, ifsc_code: e.target.value.toUpperCase() })} placeholder="e.g. HDFC0001234" maxLength={11} data-testid="bank-ifsc" />
                </div>
                <div>
                  <Label>Bank name (optional)</Label>
                  <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder="HDFC Bank" data-testid="bank-name" />
                </div>
              </>
            ) : (
              <div>
                <Label>UPI ID</Label>
                <Input value={form.upi_id} onChange={(e) => setForm({ ...form, upi_id: e.target.value.trim() })} placeholder="e.g. ramesh@okaxis or 98xxxxxxxx@ybl" data-testid="bank-upi-input" />
              </div>
            )}
            <div className="md:col-span-2 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowForm(false)} className="rounded-md">Cancel</Button>
              <Button onClick={save} disabled={busy} className="bg-[#C85A32] hover:bg-[#a8482a] text-white rounded-md" data-testid="bank-save">
                <Save size={13} className="mr-1" /> {busy ? "Saving…" : "Save"}
              </Button>
            </div>
            <div className="md:col-span-2 text-[11px] text-[#5C6B7A]">
              🔒 Stored securely. Only the last 4 digits of an account number are shown after saving. Visible only to you and LocaNest admin (for manual transfers) — never to tenants.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
